import { useEffect, useMemo, useRef, useState } from 'react';
import hrApi from '../api';

function toDateKey(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultData() {
  return {
    weekStart: null,
    weekEnd: null,
    generatedAt: null,
    kpis: {
      pendingRequests: 0,
      pendingHolidayCount: 0,
      pendingOvertimeCount: 0,
      pendingLeaves: 0,
      weeklyLeaves: 0,
      weeklyPresentCount: 0,
      weeklyAbsentCount: 0,
      weeklyHoursTotal: 0,
    },
    pendingApprovals: [],
    weeklyAvailability: [],
  };
}

export default function useManagerDashboardData({
  enabled = true,
  refreshKey = 0,
  startDate = '',
  endDate = '',
} = {}) {
  const [data, setData] = useState(defaultData);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setError('');
    if (hasLoaded.current) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    const params = {};
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    hrApi.get('/api/manager/dashboard', { params })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data || {};
        setData({
          weekStart: payload.weekStart || null,
          weekEnd: payload.weekEnd || null,
          generatedAt: payload.generatedAt || null,
          kpis: {
            pendingRequests: Number(payload?.kpis?.pendingRequests) || 0,
            pendingHolidayCount: Number(payload?.kpis?.pendingHolidayCount) || 0,
            pendingOvertimeCount: Number(payload?.kpis?.pendingOvertimeCount) || 0,
            pendingLeaves: Number(payload?.kpis?.pendingLeaves) || 0,
            weeklyLeaves: Number(payload?.kpis?.weeklyLeaves) || 0,
            weeklyPresentCount: Number(payload?.kpis?.weeklyPresentCount) || 0,
            weeklyAbsentCount: Number(payload?.kpis?.weeklyAbsentCount) || 0,
            weeklyHoursTotal: Number(payload?.kpis?.weeklyHoursTotal) || 0,
          },
          pendingApprovals: Array.isArray(payload.pendingApprovals) ? payload.pendingApprovals : [],
          weeklyAvailability: Array.isArray(payload.weeklyAvailability) ? payload.weeklyAvailability : [],
        });
        hasLoaded.current = true;
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Failed to load dashboard.');
      })
      .finally(() => {
        if (!cancelled) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      });
    return () => { cancelled = true; };
  }, [enabled, refreshKey, startDate, endDate]);

  const weekLabel = useMemo(() => {
    if (!data.weekStart || !data.weekEnd) return '';
    const from = new Date(data.weekStart);
    const to = new Date(data.weekEnd);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
    return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${to.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }, [data.weekStart, data.weekEnd]);

  const availabilityByDate = useMemo(() => {
    const byDate = {};
    (data.weeklyAvailability || []).forEach((item) => {
      if (item?.date) byDate[toDateKey(item.date)] = item;
    });
    return byDate;
  }, [data.weeklyAvailability]);

  return {
    data,
    loading: initialLoading,
    refreshing,
    error,
    weekLabel,
    availabilityByDate,
  };
}
